import React from "react";
import Reveal from "../components/Reveal";
import Counter from "../components/Counter";
import { WhatsAppCTA } from "../components/Buttons";
import {
  PartnerMailIconBtn,
  LinkedinIconBtn,
  PhoneIconBtn,
  MailIconBtn,
  PartnerPhoneIconBtn,
} from "../components/ContactIcons";
import { SITE } from "../lib/site";
import useSEO from "../hooks/useSEO";

const APPROACH = [
  {
    n: "01",
    title: "Ownership",
    body: "We don't pass work down the chain or outward without staying involved. What we take on, we see through.",
  },
  {
    n: "02",
    title: "Structure",
    body: "Filings, audits, and notices are handled through defined processes; not last-minute escalation.",
  },
  {
    n: "03",
    title: "Single Point of Contact",
    body: "Whether it involves a CA, company secretary, lawyer, or valuer, you deal with one team that coordinates everything.",
  },
];

function PartnerBlock({ partner, side = "left" }) {
  const flip = side === "right";
  return (
    <article
      className="grid grid-cols-12 gap-8 md:gap-12 py-16 md:py-20 border-t border-[var(--border-light)]"
      data-testid={`partner-${partner.slug}`}
    >
      <div className={`col-span-12 md:col-span-5 ${flip ? "md:order-2" : ""}`}>
        <div className="relative">
          <div className="absolute -inset-3 bg-voltage/15 rounded-xl rotate-[-1.5deg]" aria-hidden />
          <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-surface">
            <img src={partner.photo} alt={partner.name} loading="lazy" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
      <div className={`col-span-12 md:col-span-7 ${flip ? "md:order-1" : ""} flex flex-col justify-center`}>
        <div className="eyebrow mb-3">{partner.role}</div>
        <div className="flex items-stretch">
          <span aria-hidden className="w-[3px] h-8 md:h-10 bg-voltage shrink-0 mr-3" data-testid={`partner-bar-${partner.slug}`} />
          <h3 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.05] tracking-[-0.025em]">
            {partner.name}
          </h3>
        </div>
        <div className="mt-1 text-slate-muted text-[13px]">{partner.credentials}</div>

        <p className="mt-6 text-slate-muted text-[15px] md:text-[16px] leading-[1.65] max-w-2xl">
          {partner.bio}
        </p>

        <div className="mt-7">
          <div className="eyebrow mb-3">Areas of focus</div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl">
            {partner.focus.map((f) => (
              <li key={f} className="flex items-baseline gap-3 text-[14px] text-ink/85">
                <span className="w-1.5 h-1.5 rounded-full bg-voltage shrink-0 translate-y-1" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <PartnerPhoneIconBtn
            phoneTel={partner.phoneTel}
            phoneDisplay={partner.phoneDisplay}
            variant="light"
            testId={`partner-phone-${partner.slug}`}
          />
          <PartnerMailIconBtn
            email={partner.email}
            variant="light"
            testId={`partner-email-${partner.email}`}
          />
          <LinkedinIconBtn
            href={partner.linkedin}
            variant="light"
            testId={`partner-linkedin-${partner.slug}`}
            label={`${partner.name} on LinkedIn`}
          />
        </div>
      </div>
    </article>
  );
}

export default function About() {
  const advisor = SITE.advisor;
  useSEO({
    title: "About SVMD & Associates — Founding Partners & Senior Advisor",
    description:
      "Founded in Coimbatore, SVMD & Associates serves 1,000+ clients across India. Meet founding partners Sri Vaibhav V (ACA) and Mithuna D V (ACA), and senior advisor CA Padmanabhan R (FCA, DISA).",
    keywords:
      "SVMD partners, Sri Vaibhav CA, Mithuna D V CA, Padmanabhan CA Coimbatore, CA firm leadership, audit partners Coimbatore",
    canonical: "/about",
  });
  return (
    <>
      {/* HERO (DARK with real Coimbatore office image) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <img
          src="/about-hero.jpg"
          alt="SVMD & Associates office in Coimbatore"
          data-testid="about-hero-image"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(0.85) brightness(0.9)" }}
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,22,40,0.7) 0%, rgba(10,22,40,0.85) 100%)",
          }}
          aria-hidden
        />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 pt-28 md:pt-36 pb-24 md:pb-28">
          <Reveal>
            <div className="eyebrow eyebrow-voltage mb-6">About</div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-semibold text-[36px] sm:text-[52px] md:text-[72px] leading-[1.04] tracking-[-0.03em] max-w-5xl">
              We work with{" "}
              <span className="text-voltage">
                <Counter to={1000} suffix="+" />
              </span>{" "}
              clients across India.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 max-w-2xl text-paper/85 text-base md:text-[18px] leading-[1.55]">
              Here&rsquo;s how, and who. A practice built around ownership,
              structure, and a single point of contact.
            </p>
          </Reveal>
        </div>
      </section>

      {/* INTRO (LIGHT) */}
      <section className="bg-paper text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <Reveal className="col-span-12 md:col-span-4">
              <div className="eyebrow mb-4">Origin</div>
              <h2 className="font-display font-semibold text-[26px] md:text-[34px] leading-[1.1] tracking-tight">
                Coimbatore, India.<br />Across the country.
              </h2>
            </Reveal>
            <Reveal delay={120} className="col-span-12 md:col-span-8 space-y-5 text-[15px] md:text-[16px] text-slate-muted leading-[1.7]">
              <p>
                SVMD &amp; Associates is a Chartered Accountant firm based in
                Coimbatore, working with businesses across India. We started
                with a simple intent &mdash; to build a practice that
                businesses can rely on without having to follow up, clarify,
                or worry about what&rsquo;s been missed.
              </p>
              <p>
                Over time, that has shaped how we work. The profession often
                tends to be reactive: driven by deadlines, filings, and
                notices. We take a more structured approach. We take
                ownership, build systems around the work, and stay involved so
                that things run as they should.
              </p>
              <p>
                For our clients, this means one less area to think about
                &mdash; whether it is compliance, audit, or ongoing financial
                matters.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OUR APPROACH (DARK) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-80" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <Reveal className="max-w-2xl">
              <div className="eyebrow eyebrow-voltage mb-4">Our approach</div>
              <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                Three principles, applied to every engagement.
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {APPROACH.map((p, i) => (
              <Reveal key={p.n} delay={i * 100}>
                <div
                  data-testid={`approach-${p.n}`}
                  className="glass-panel rounded-xl p-7 md:p-8 h-full"
                >
                  <div className="font-display text-[64px] md:text-[80px] leading-none tracking-[-0.04em]" style={{ WebkitTextStroke: "1px rgba(212,255,63,0.7)", color: "transparent" }}>
                    {p.n}
                  </div>
                  <h3 className="mt-3 font-display text-[20px] md:text-[22px] text-paper">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-paper/65 text-[14px] leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP (LIGHT, alternating) */}
      <section className="bg-paper text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-8">
          <Reveal>
            <div className="eyebrow mb-4">Leadership</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em] max-w-3xl">
              Founding partners.
            </h2>
          </Reveal>

          <div className="mt-10">
            {SITE.partners.map((p, i) => (
              <PartnerBlock key={p.slug} partner={p} side={i % 2 === 0 ? "left" : "right"} />
            ))}
          </div>
        </div>
      </section>

      {/* SENIOR ADVISOR (DARK with stat) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" aria-hidden />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.22)_0%,transparent_55%)]" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="eyebrow eyebrow-voltage mb-4">Senior advisor</div>
          </Reveal>

          <div className="grid grid-cols-12 gap-8 md:gap-12 items-start mt-4">
            <Reveal delay={100} className="col-span-12 md:col-span-5">
              <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                {advisor.name}
              </h2>
              <div className="mt-2 text-paper/70 text-[13px]">{advisor.credentials}</div>

              <div className="mt-10 border-t border-white/10 pt-7 flex items-end gap-6">
                <div className="font-display font-semibold text-[96px] md:text-[128px] leading-none tracking-[-0.04em] text-voltage">
                  <Counter to={Number(advisor.yearsLabel)} suffix="" />
                </div>
                <div className="pb-3 text-paper/70 text-[14px] tracking-wide leading-snug">
                  {advisor.yearsCaption}<br />of practice
                </div>
              </div>
            </Reveal>

            <Reveal delay={180} className="col-span-12 md:col-span-7">
              <p className="text-paper/80 text-[15px] md:text-[16px] leading-[1.7] max-w-3xl">
                {advisor.bio}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CLOSE CTA */}
      <section className="bg-paper text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24">
          <Reveal>
            <h2 className="font-display font-semibold text-[32px] md:text-[44px] leading-[1.06] tracking-[-0.025em] max-w-3xl">
              Want to talk to a partner directly?
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <WhatsAppCTA testId="about-close-whatsapp-cta" />
              <PhoneIconBtn variant="light" testId="about-close-phone-icon" />
              <MailIconBtn variant="light" testId="about-close-email-icon" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
